import {
  wixCliLoginEnd,
  wixCliLoginStart,
} from '@wix/bi-logger-dev-tools-data/v2';
import type { BiLogger } from '@wix/cli-telemetry';
import { unifiedAuthStrategy } from './auth-strategies/unified-auth-strategy.js';
import type {
  AuthStrategy,
  AuthStrategyState,
} from './auth-strategies/auth-strategy-types.js';
import { requestDeviceCode } from './device-code.js';
import type { DeviceCodeResponse, UserInfo } from './schemas.js';
import { buildVerificationUris } from './verfication-uri.js';

export type LoginFlowMessage =
  | {
      email: string;
      event: 'logged_in' | 'success';
      userId: string;
    }
  | {
      event: 'awaiting_user';
      expiresInSeconds: number;
      userCode: string;
      verificationUri: string;
    };

export function emitLoginFlowMessage(event: LoginFlowMessage): void {
  // eslint-disable-next-line no-console
  console.log(`${JSON.stringify(event)}\n`);
}

export function createAwaitingUserLoginFlowMessage(
  deviceCode: DeviceCodeResponse
): LoginFlowMessage {
  const { coloredVerificationUri } = buildVerificationUris(
    deviceCode.verificationUri
  );

  return {
    event: 'awaiting_user',
    expiresInSeconds: deviceCode.expiresIn,
    userCode: deviceCode.userCode,
    verificationUri: coloredVerificationUri,
  };
}

export function createLoggedInLoginFlowMessage(
  userInfo: UserInfo
): LoginFlowMessage {
  return {
    email: userInfo.email,
    event: 'logged_in',
    userId: userInfo.userId,
  };
}

export function createSuccessfulLoginFlowMessage(
  userInfo: UserInfo
): LoginFlowMessage {
  return {
    email: userInfo.email,
    event: 'success',
    userId: userInfo.userId,
  };
}

function reportFailedLogin(biLogger: BiLogger, error: unknown) {
  void biLogger.report(
    wixCliLoginEnd({
      isSuccess: false,
      ...(error instanceof Error
        ? {
            errorType: error.name,
          }
        : {
            errorType: 'unknown',
            errorMessage: JSON.stringify(error),
          }),
    })
  );
}

export async function runAgentLoginFlow({
  biLogger,
  loginFn = unifiedAuthStrategy.login,
  signal = new AbortController().signal,
}: {
  biLogger: BiLogger;
  loginFn?: AuthStrategy['login'];
  signal?: AbortSignal;
}): Promise<AuthStrategyState> {
  void biLogger.report(wixCliLoginStart({}));

  try {
    const deviceCode = await requestDeviceCode();

    emitLoginFlowMessage(createAwaitingUserLoginFlowMessage(deviceCode));

    const authData = await loginFn({
      deviceCode,
      signal,
    });

    void biLogger.report(wixCliLoginEnd({ isSuccess: true }));

    emitLoginFlowMessage(createSuccessfulLoginFlowMessage(authData.userInfo));

    return authData;
  } catch (error) {
    reportFailedLogin(biLogger, error);
    throw error;
  }
}
